/* eslint-disable react/jsx-handler-names */
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import Icon from 'eventbrite_design_system/icon/Icon';
import Button from 'eventbrite_design_system/button/Button';
import ChevronDownChunkySvg from 'eventbrite_design_system/iconography/icons/ChevronDownChunky';
import ChevronUpChunkySvg from 'eventbrite_design_system/iconography/icons/ChevronUpChunky';
import EBPropTypes from 'js-utils/prop-types';

import AnimatedFilters from './AnimatedFilters';

import './FilterSection.scss';

const isFirstValueBelowTheFold = (value, croppedRadioValues) =>
    value === croppedRadioValues[0].value;

// eslint-disable-next-line react/no-multi-comp
class FilterSection extends React.Component {
    static propTypes = {
        /**
         * The text title of this section of filters
         */
        title: EBPropTypes.translation.isRequired,
        /**
         * The filter options to be presented to a user
         */
        filters: PropTypes.object.isRequired,
        /**
         * Title should not be shown when panel is opened with just one filter type
         */
        shouldHideTitle: PropTypes.bool.isRequired,
        /**
         * Callback to trigger when users pick a different filter
         */
        onOptionChange: PropTypes.func.isRequired,
        /**
         * The window location provided by withRouter
         */
        location: PropTypes.object.isRequired,
        /**
         * The text to present on the more button.  e.g. "More categories"
         */
        moreLabel: EBPropTypes.translation,
        /**
         * The text to present on the less button when filters are expanded.  e.g. "Fewer categories"
         */
        lessLabel: EBPropTypes.translation,
        /**
         * The number of options to present before we hide remaining options behind a "more" button
         */
        moreThreshold: PropTypes.number,
        /**
         * Relation between url parameters and the section's options
         */
        urlParameterMapping: PropTypes.object,
    };

    state = {
        // the currently selected filter in this section
        selectedFilter: Object.keys(this.props.filters)[0],

        // show the whole filter section (vs collapsing part of it)
        expand: false,
    };

    componentDidMount() {
        // when the page is loaded for the first time, we check the url to see whether
        // a filter should be set in the radio buttons
        this._parseUrl(this.props.location, false);
    }

    componentDidUpdate(prevProps) {
        // if the user clicks the back or forward button, we check the new url to see
        // if a filter needs to be applied
        if (this.props.location !== prevProps.location) {
            this._parseUrl(this.props.location, true);
        }
    }

    /**
     * Examine the url and apply any filters that are present in the url
     *
     * @param {string} url - the url of this page
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     *
     */
    _parseUrl = (location, shouldSearch) => {
        const { urlParameterMapping } = this.props;
        const url = location.pathname;

        if (urlParameterMapping) {
            const urlSplit = url.split('/');

            if (urlSplit.length > 3) {
                // individual filters are separated by double dashes
                const urlParams = urlSplit[3].split('--');

                // check every possible filter to see whether it exists in this url
                for (const key in urlParameterMapping) {
                    if (urlParams.includes(key)) {
                        this._handleOptionChange(
                            urlParameterMapping[key],
                            shouldSearch,
                            false,
                        );

                        // only one filter at a time so return when we find one
                        return;
                    }
                }

                // if we can't find any filters in the url, we apply the 'all' filter
                this._handleOptionChange('all', shouldSearch, false);
            }
        }
    };

    /**
     * Handle a change of the filter setting. This is triggered in three cases.
     *
     * 1. The initial page load. We want to set the radio value but not trigger a new search
     * because the server has already included the search results in the html.
     *
     * 2. The back or forward button is pressed. We want to set the radio button and trigger
     * a new search but we don't want to update the url path location.
     *
     * 3. The user has selected a new radio option.  We want to set the radio button, trigger
     * a new search, and update the url path to represent the new option state.
     *
     * @param {string} value - the new selected value to be set for the radio buttons
     * @param {bool} shouldSearch - should we make an ajax search request to the server
     * @param {bool} shouldUpdateRoute - should we set a new location in the url
     *
     */
    _handleOptionChange = (value, shouldSearch, shouldUpdateRoute) => {
        const { onOptionChange } = this.props;

        if (value !== this.state.selectedFilter) {
            this.setState({ selectedFilter: value });
            if (shouldSearch) {
                onOptionChange(value, shouldUpdateRoute);
            }
        }
    };

    /**
     * Toggle the expand state specifying whether the whole filter section should be shown
     */
    _handleToggleExpand = () => {
        this.setState((prevState) => ({ expand: !prevState.expand }));
    };

    render() {
        const {
            title,
            shouldHideTitle,
            filters,
            moreLabel,
            lessLabel,
            moreThreshold,
        } = this.props;

        // if we're not in an "expand" state, we only show the first x filters (x = moreThreshold)
        const entries = Object.entries(filters);
        const filtersCropped = entries.slice(0, moreThreshold);
        const filterCroppedRemaining = entries.slice(
            moreThreshold,
            entries.length,
        );

        const croppedRadioValues = filtersCropped.map(([key, label]) => ({
            display: label,
            value: key,
        }));
        const croppedRemainingRadioValues = filterCroppedRemaining.map(
            ([key, label]) => ({
                display: label,
                value: key,
            }),
        );
        const allRadioValues = [
            ...croppedRadioValues,
            ...croppedRemainingRadioValues,
        ];

        let buttonLabel = moreLabel;

        let buttonIconType = <ChevronDownChunkySvg />;

        if (this.state.expand) {
            buttonLabel = lessLabel;
            buttonIconType = <ChevronUpChunkySvg />;
        }

        return (
            <div className="filter-section" data-integration="filter-section">
                <Button
                    type="button"
                    style="none"
                    disabled={!moreThreshold}
                    onClick={this._handleToggleExpand}
                    __containerClassName="filter-section__title"
                >
                    {!shouldHideTitle && title}
                    {allRadioValues.length >= moreThreshold && (
                        <span className="eds-l-mar-left-2">
                            <Icon type={buttonIconType} color="grey-700" />
                        </span>
                    )}
                </Button>
                <AnimatedFilters
                    allFilters={allRadioValues}
                    croppedFilters={croppedRadioValues}
                    expand={this.state.expand}
                    selectedFilter={this.state.selectedFilter}
                    moreThreshold={moreThreshold}
                    renderRadios={(radios) =>
                        radios.map(({ display, value }) => (
                            <div key={value}>
                                {// Show a border when expanded to separate "above the fold" and "below the fold"
                                // Don't need divider if there is no "more threshold" (meaning all filters are shown)
                                moreThreshold &&
                                    isFirstValueBelowTheFold(
                                        value,
                                        croppedRemainingRadioValues,
                                    ) && (
                                        <div className="filter-section__filter-divider" />
                                    )}
                                <Button
                                    data-integration={`filter-section-button-${value}`}
                                    style="none"
                                    __containerClassName={classnames(
                                        'filter-section__button',
                                        `filter-section__button-${
                                            this.state.selectedFilter === value
                                                ? 'selected'
                                                : 'unselected'
                                        }`,
                                    )}
                                    onClick={() =>
                                        this._handleOptionChange(
                                            value,
                                            true,
                                            true,
                                        )
                                    }
                                >
                                    <span>{display}</span>
                                </Button>
                            </div>
                        ))
                    }
                />
                {moreThreshold && (
                    <Button
                        type="button"
                        style="none"
                        onClick={this._handleToggleExpand}
                        data-integration="filter-section__more"
                        __containerClassName="filter-section__more eds-text-weight--heavy"
                    >
                        {buttonLabel}
                    </Button>
                )}
            </div>
        );
    }
}

// withRouter adds a location field to props which allows us to watch for url changes
export default withRouter(FilterSection);
